import React, { useMemo } from 'react'
import { ControlledTextArea } from 'transfix-form/components'
import { ModalBase, IModalBaseProps, MODAL_TYPES } from 'transfix-ui/components/Modals'
import { Box } from 'transfix-ui/core'
import { IParserVersionSchema } from '../ParserVersionSchema'
import { createParserVersion, deleteParserVersion, updateParserVersion } from '../api'
import { parserVersionRequest, ParserVersion, Parser } from '../types'
import { useParserVersionConfirmationForm } from './useParserVersionConfirmationForm'

export type TParserVersionModalType = 'create' | 'copy' | 'delete' | 'edit'

interface IParserVersionConfirmationModalProps extends Omit<IModalBaseProps, 'modalType'> {
  onClose: () => void
  onSuccess: () => void
  type: TParserVersionModalType
  selectedParser?: Parser
  /**
   * Parser that has been loaded/being currently edited.
   * @example { id: 1, is_live: false, was_ever_live: false, extraction_template: 'Some jsonata query', conversion_instructions: 'Some json'}
   */
  selectedParserVersion?: ParserVersion
  /**
   * Is this creating a parser version as a copy of an existing parser version?
   * @example true
   */
  setSelectedParserVersion: React.Dispatch<React.SetStateAction<ParserVersion | undefined>>
}

export const ParserVersionConfirmationModal = ({
  onClose,
  onSuccess,
  selectedParser,
  selectedParserVersion,
  setSelectedParserVersion,
  type,
  ...props
}: IParserVersionConfirmationModalProps) => {
  const methods = useParserVersionConfirmationForm()

  const { control, handleSubmit } = methods

  const title =
    type === 'delete'
      ? 'Are you sure you want to delete this parser version?'
      : 'Create a new version for this parser?'

  const copyTitle = `Create a new version for this parser as a copy of ${selectedParserVersion?.parent_parser?.name} version ${selectedParserVersion?.version_number}`

  const editTitle = `Edit the version notes on this parser version?`

  const modalTitle = useMemo(() => {
    if (type === 'copy') {
      return copyTitle
    }
    if (type === 'create' || type === 'delete') {
      return title
    }

    return editTitle
  }, [type])

  const saveParserVersion = async (formParams: IParserVersionSchema) => {
    const body: parserVersionRequest = {
      configurable_parser_version: {
        edi_mapper_configurable_parser_id: selectedParser?.id || 0,
        is_live: false,
        parser_library: 'stupidedi',
        stupidedi_config: 'default',
        edi_mapper_tfx12_definition_id: '',
        extraction_template: '',
        conversion_instructions: JSON.parse('{}'),
        element_conversion: 'default',
        document_conversion: 'default',
        notes: formParams.notes || '',
      },
    }
    createParserVersion(body)
  }

  const updateParserVersionNotes = async (formParams: IParserVersionSchema) => {
    const body: parserVersionRequest = {
      configurable_parser_version: {
        notes: formParams.notes || '',
      },
    }
    const response = await updateParserVersion(body, selectedParserVersion)
    if (selectedParserVersion) {
      selectedParserVersion.notes = response.notes
    }
  }

  const copyParserVersion = async (formParams: IParserVersionSchema) => {
    const body: parserVersionRequest = {
      configurable_parser_version: {
        edi_mapper_configurable_parser_id: selectedParserVersion?.parent_parser?.id || 0,
        is_live: false,
        parser_library: selectedParserVersion?.parser_library,
        stupidedi_config: selectedParserVersion?.stupidedi_config,
        edi_mapper_tfx12_definition_id: selectedParserVersion?.edi_mapper_tfx12_definition_id,
        extraction_template: selectedParserVersion?.extraction_template,
        conversion_instructions: selectedParserVersion?.conversion_instructions,
        element_conversion: selectedParserVersion?.element_conversion,
        document_conversion: selectedParserVersion?.document_conversion,
        notes: formParams.notes || selectedParserVersion?.notes,
      },
    }
    createParserVersion(body)
  }

  const deleteVersion = async () => {
    await deleteParserVersion(selectedParserVersion?.id || 0)
    setSelectedParserVersion(undefined)
  }

  const confirmSaveHandler = async (formParams: IParserVersionSchema) => {
    await saveParserVersion(formParams)
    onSuccess()
    setSelectedParserVersion(undefined)
    onClose()
  }

  const editVersionHandler = async (formParams: IParserVersionSchema) => {
    await updateParserVersionNotes(formParams)
    onSuccess()
    onClose()
  }

  const copyVersionHandler = async (formParams: IParserVersionSchema) => {
    await copyParserVersion(formParams)
    onSuccess()
    setSelectedParserVersion(undefined)
    onClose()
  }

  const confirmDeleteHandler = async () => {
    await deleteVersion()
    onSuccess()
    onClose()
  }

  const submitHandler = handleSubmit((formParams: IParserVersionSchema) => {
    if (type === 'copy') {
      copyVersionHandler(formParams)
    } else if (type === 'delete') {
      confirmDeleteHandler()
    } else if (type === 'edit') {
      editVersionHandler(formParams)
    } else {
      confirmSaveHandler(formParams)
    }
  })

  const actions: IModalBaseProps['actions'] = [
    {
      label: 'Confirm',
      action: submitHandler,
      type: 'confirm',
      dataTest: 'confirm-button',
    },
    {
      label: 'Cancel',
      type: 'dismiss',
      dataTest: 'cancel-button',
    },
  ]

  return (
    <ModalBase
      onClose={onClose}
      modalType={MODAL_TYPES.form}
      size={2}
      title={modalTitle}
      open
      closeOnBackdropClick
      actions={actions}
      {...props}>
      {type !== 'delete' && (
        <Box mb={2}>
          <ControlledTextArea
            control={control}
            name='notes'
            defaultValue={selectedParserVersion?.notes}
            TextAreaProps={{
              size: 'medium',
              label: 'Notes',
              placeholder: 'Notes',
            }}
          />
        </Box>
      )}
    </ModalBase>
  )
}
